import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Input,
  Text,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components';
import { DepartmentType, InterviewType } from '@/services/api';

import { departmentLabel } from '../board/DepartmentLabel';
import type {
  NewInterviewFormInputs,
  NewInterviewFormStepProps,
} from './common';

export const StepInterviewInformation = ({
  isActive,
}: NewInterviewFormStepProps) => {
  const { t } = useTranslation(['interview', 'common', 'components']);
  const form = useFormContext<NewInterviewFormInputs>();
  const {
    register,
    formState: { errors },
  } = form;

  if (!isActive) return null;

  return (
    <Flex flexDir="column" gap={4}>
      <Heading fontSize="2xl">
        {t('interview:createNewInterview.interviewInformation.stepTitle')}
      </Heading>
      <Text>
        {t('interview:createNewInterview.interviewInformation.description')}
      </Text>
      <FormField
        type="text"
        label={`${t('interview:createNewInterview.interviewInformation.interviewTitle')} *`}
        rules={{
          required: t(
            'interview:createNewInterview.interviewInformation.interviewTitleRequired'
          ),
          validate: (value) => {
            if (value && value?.trim().length < 3) {
              return t(
                'interview:createNewInterview.interviewInformation.interviewTitleMinLength'
              );
            }
          },
        }}
        placeholder={t(
          'interview:createNewInterview.interviewInformation.interviewTitlePlaceHolder'
        )}
        control={form.control}
        name="title"
      />

      <HStack>
        <FormField
          type="select"
          optionalityHint="required"
          options={interviewTypes}
          label={t(
            'interview:createNewInterview.questionPreparation.interviewType'
          )}
          rules={{
            required: t(
              'interview:createNewInterview.questionPreparation.interviewTypeRequired'
            ),
          }}
          placeholder={t(
            'interview:createNewInterview.questionPreparation.selectInterviewType'
          )}
          control={form.control}
          name="type"
        />

        <FormField
          type="select"
          optionalityHint="required"
          label={t('components:board.department')}
          control={form.control}
          name="department"
          rules={{
            required: t(
              'interview:createNewInterview.questionPreparation.departmentRequired'
            ),
          }}
          options={Object.values(DepartmentType).map((department) => ({
            label: departmentLabel(department),
            value: department,
          }))}
          placeholder={t('common:actions.pleaseSelect')}
          size="md"
        />
      </HStack>

      <HStack spacing={4}>
        <FormField
          type="date"
          dayPickerProps={{
            arePastDaysDisabled: true,
            placeholder: t(
              'common:timeAndDate.datePlaceHolder'
            ).toAppLocaleUpperCase(),
          }}
          label={`${t('common:timeAndDate.startDate')} *`}
          rules={{
            required: t('common:timeAndDate.startDateRequired'),
          }}
          control={form.control}
          name="startDate"
        />

        <FormControl isInvalid={!!errors.startTime}>
          <FormLabel htmlFor="startTime">{`${t('common:timeAndDate.startTime')} *`}</FormLabel>
          <Input
            id="startTime"
            type="time"
            {...register('startTime', {
              required: t('common:timeAndDate.startTimeRequired'),
            })}
          />
          <FormErrorMessage>
            {errors.startTime && errors.startTime.message}
          </FormErrorMessage>
        </FormControl>
      </HStack>
      <HStack spacing={4}>
        <FormField
          type="date"
          label={`${t('common:timeAndDate.endDate')} *`}
          rules={{
            required: t('common:timeAndDate.endDateRequired'),
          }}
          dayPickerProps={{
            arePastDaysDisabled: true,
          }}
          control={form.control}
          name="endDate"
        />

        <FormControl isInvalid={!!errors.endTime}>
          <FormLabel htmlFor="endTime">
            {t('common:timeAndDate.endTime')}
          </FormLabel>
          <Input
            id="endTime"
            type="time"
            {...register('endTime', {
              required: t('common:timeAndDate.endTimeRequired'),
            })}
          />
          <FormErrorMessage>
            {errors.endTime && errors.endTime.message}
          </FormErrorMessage>
        </FormControl>
      </HStack>
    </Flex>
  );
};

type interviewType = {
  label: string;
  value: InterviewType;
};

export const interviewTypes: Array<interviewType> = [
  {
    label: t(
      'interview:createNewInterview.questionPreparation.jobInterviewType'
    ),
    value: InterviewType.JobInterview,
  },
  {
    label: t(
      'interview:createNewInterview.questionPreparation.technicalInterviewType'
    ),
    value: InterviewType.TechnicalInterview,
  },
  {
    label: t(
      'interview:createNewInterview.questionPreparation.exitInterviewType'
    ),
    value: InterviewType.ExitInterview,
  },
  {
    label: t(
      'interview:createNewInterview.questionPreparation.languageInterviewType'
    ),
    value: InterviewType.LanguageTest,
  },
  {
    label: t(
      'interview:createNewInterview.questionPreparation.roleBasedInterviewType'
    ),
    value: InterviewType.RoleBased,
  },
  {
    label: t(
      'interview:createNewInterview.questionPreparation.interviewAssistantInterviewType'
    ),
    value: InterviewType.InterviewAssistant,
  },
];
